import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { faForwardStep, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import type { Database } from '../../lib/database.types';
import { Badge, Avatar, KebabMenu } from '../ui';
import AdsBadge from './AdsBadge';
import { formatMoney, daysInStage, isAdsLead, leadOrigin } from './meta';

// The DB enum still carries 'contacted' and 'negotiation' (Postgres can't drop enum values
// without recreating the type); the pipeline stopped using them on 2026-09-16.
export type ProspectStage = 'leads' | 'qualified' | 'proposal' | 'closed_won' | 'closed_lost';

type ProspectRow = Database['public']['Tables']['prospects']['Row'];

/**
 * A prospects row as the sales screens use it. Same columns as the DB; `stage` is the
 * narrower set the pipeline still uses and `tags` is never null (column default '{}').
 * Rows are cast to this at the fetch boundary in Sales.tsx.
 */
export type Prospect = Omit<ProspectRow, 'stage' | 'tags'> & {
  stage: ProspectStage;
  tags: string[];
};
export type ProspectInsert = Database['public']['Tables']['prospects']['Insert'];

export interface Stage {
  id: ProspectStage;
  name: string;
  prospects: Prospect[];
}

interface DealCardProps {
  prospect: Prospect;
  onEdit: (prospect: Prospect) => void;
  onDelete: (id: string) => void;
  onAdvance: (prospect: Prospect) => void;
}

/** Shared card face. `preview` drops the interactive bits for the drag overlay clone. */
function DealCardBody({
  prospect,
  preview = false,
  onEdit,
  onDelete,
  onAdvance,
}: {
  prospect: Prospect;
  preview?: boolean;
  onEdit?: (prospect: Prospect) => void;
  onDelete?: (id: string) => void;
  onAdvance?: (prospect: Prospect) => void;
}) {
  const isClosed = prospect.stage.startsWith('closed');
  // The Ads pill is derived from click ids; drop the site's literal "ads" tag so it isn't shown twice.
  const tags = isAdsLead(prospect) ? prospect.tags.filter((t) => t.toLowerCase() !== 'ads') : prospect.tags;
  const visibleTags = tags.slice(0, 3);
  const origin = leadOrigin(prospect);
  const hiddenTagCount = tags.length - visibleTags.length;

  return (
    <>
      {/* Top row: company + actions */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        <span
          style={{
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
            color: 'var(--text-body)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {prospect.company_name}
        </span>
        {preview ? null : (
          <KebabMenu
            label="Deal actions"
            actions={[
              !isClosed && onAdvance
                ? { label: 'Advance Stage', icon: faForwardStep, onClick: () => onAdvance(prospect) }
                : null,
              onEdit ? { label: 'Edit Deal', icon: faPen, onClick: () => onEdit(prospect) } : null,
              onDelete ? { label: 'Delete', icon: faTrash, danger: true, onClick: () => onDelete(prospect.id) } : null,
            ]}
          />
        )}
      </div>

      {/* Deal value + source */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '8px',
          marginBottom: '8px',
        }}
      >
        <span
          className="t-num"
          style={{
            fontSize: 'var(--text-md)',
            fontWeight: 'var(--weight-semibold)' as React.CSSProperties['fontWeight'],
            letterSpacing: 'var(--tracking-tight)',
            color: 'var(--text-body)',
          }}
        >
          {formatMoney(prospect.deal_value)}
        </span>
        <AdsBadge prospect={prospect} />
      </div>

      {/* Where the lead first came from: source · landing page · first visit · days to lead */}
      {origin ? (
        <div title={origin.title} style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)', marginBottom: '8px', minWidth: 0 }}>
          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{origin.line}</div>
          {origin.visit ? <div className="t-num" style={{ marginTop: '2px' }}>{origin.visit}</div> : null}
        </div>
      ) : null}

      {/* Tags, only when there are any */}
      {visibleTags.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginBottom: '8px' }}>
          {visibleTags.map((tag) => (
            <Badge key={tag} tone="neutral">
              {tag}
            </Badge>
          ))}
          {hiddenTagCount > 0 && (
            <span className="t-num" style={{ fontSize: 'var(--text-2xs)', color: 'var(--text-faint)' }}>
              +{hiddenTagCount}
            </span>
          )}
        </div>
      )}

      {/* Footer: contact + days-in-stage + owner */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
        <span
          style={{
            fontSize: 'var(--text-xs)',
            color: 'var(--text-faint)',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {prospect.contact_name}
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', flexShrink: 0 }}>
          <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }} title="Time in stage">
            {daysInStage(prospect.stage_entered_at)}
          </span>
          {prospect.assigned_to_name ? <Avatar name={prospect.assigned_to_name} size={18} /> : null}
        </span>
      </div>
    </>
  );
}

const cardSurface: React.CSSProperties = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-hairline)',
  borderRadius: 'var(--radius-md)',
  padding: '12px 14px',
};

/**
 * The clone that follows the pointer while a card is dragged (rendered through
 * DndContext's DragOverlay in a portal, so no column or scroll container can clip it).
 */
export function DealCardPreview({ prospect }: { prospect: Prospect }) {
  return (
    <div style={{ ...cardSurface, boxShadow: 'var(--shadow-hover)', borderColor: 'var(--border-strong)', cursor: 'grabbing' }}>
      <DealCardBody prospect={prospect} preview />
    </div>
  );
}

export default function DealCard({ prospect, onEdit, onDelete, onAdvance }: DealCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: prospect.id,
  });

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className="hover-lift select-none"
      // A plain click opens the drawer. Once a drag activates (8px of mouse travel, or a
      // 250ms touch hold) dnd-kit stops the click at the document, so drops never open it.
      onClick={() => onEdit(prospect)}
      style={{
        ...cardSurface,
        transform: CSS.Transform.toString(transform),
        transition,
        // While dragging, the DragOverlay clone is the visible card; this one stays as a faint
        // placeholder so the column shows where it came from.
        opacity: isDragging ? 0.3 : 1,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'pointer',
        touchAction: 'manipulation',
      }}
    >
      <DealCardBody prospect={prospect} onEdit={onEdit} onDelete={onDelete} onAdvance={onAdvance} />
    </div>
  );
}
